import { useCallback, useMemo } from 'react';
import { UTCDate } from '@date-fns/utc';
import BigNumber from 'bignumber.js';
import { BarChart, type BarDatumRecord } from '@/shared/components/charts/BarChart';
import { ExportCsvButton } from '@/shared/components/ExportCSVButton';
import { ChartLegend } from '@/shared/components/molecules/ChartLegend';
import { type ChartAggregationIntervalOption } from '@/shared/hooks/useChartAggregation';
import { getTooltipDateLabel } from '@/shared/utils/date';
import { FLIP_SYMBOL } from '@/shared/utils/env';
import TokenAmount from '@/shared/utils/TokenAmount';

type ChartDatum = {
  date: string;
  balance: string;
};

const color = '#46DA93';

const BarTooltip = ({
  data,
  aggregationInterval,
  bottom,
}: {
  data: BarDatumRecord;
  aggregationInterval: ChartAggregationIntervalOption['value'];
  bottom: BigNumber;
}) => (
  <div className="flex w-[260px] flex-col font-aeonikMedium text-12 text-cf-light-2">
    <div className="font-aeonikMedium text-12">
      {getTooltipDateLabel({
        date: new UTCDate(data.date),
        aggregationInterval,
      })}
    </div>
    <span className="text-20 text-white">
      {TokenAmount.fromWholeUnits(bottom.plus(data.balance as string), 'Flip').toCapped()}{' '}
      {FLIP_SYMBOL}
    </span>
  </div>
);

export const EmptyBalanceChartPlaceholder = () => (
  <div className="relative z-0 flex h-full w-full flex-col items-center justify-center rounded-md border border-cf-gray-4 bg-cf-gray-3 font-aeonikMedium">
    <div className="flex flex-col items-center justify-center gap-y-6">
      <div className="flex flex-col items-center justify-center gap-y-3">
        <div className="text-24 text-cf-white">No balance data yet</div>
      </div>
    </div>
    <img
      src="/bar-chart-placeholder.png"
      alt="Bar Chart Placeholder"
      className="absolute bottom-0 -z-10 block h-[300px] w-full object-cover"
    />
  </div>
);

export const BalanceChart = ({
  data: chartData,
  isLoading,
  aggregationInterval,
  exportCsvData,
  nonZeroStart,
}: {
  data: ChartDatum[];
  aggregationInterval: ChartAggregationIntervalOption['value'];
  exportCsvData?: () => Promise<void>;
  isLoading?: boolean;
  nonZeroStart?: boolean;
}) => {
  const chartDates = useMemo(
    () => [...new Set(chartData.map((item) => new Date(item.date).toISOString().split('T')[0]))],
    [chartData],
  );

  const bottom = useMemo(() => {
    if (nonZeroStart) {
      const balances = chartData.map((d) => d.balance);
      const max = BigNumber.max(...balances);
      const min = BigNumber.min(...balances.filter((b) => Number(b) > 0));
      const diff = max.minus(min).times(0.2);
      const bottomBeforeRounding = BigNumber.max(0, min.minus(diff));

      let divisor = 1;
      if (bottomBeforeRounding.gt(100_000)) {
        divisor = 10_000;
      } else if (bottomBeforeRounding.gt(10_000)) {
        divisor = 1_000;
      } else if (bottomBeforeRounding.gt(1_000)) {
        divisor = 100;
      }
      return bottomBeforeRounding.dividedToIntegerBy(divisor).multipliedBy(divisor);
    }
    return new BigNumber(0);
  }, [chartData, nonZeroStart]);

  const formattedData = useMemo(
    () =>
      bottom.eq(0)
        ? chartData
        : chartData.map((d) => ({
            date: d.date,
            balance: BigNumber.max(0, new BigNumber(d.balance).minus(bottom)).toFixed(5),
          })),
    [chartData, bottom],
  );

  const xTickFormat = useCallback(
    (dateString: string) =>
      new Date(dateString).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
      }),
    [],
  );

  const yTickFormat = useCallback(
    (value: number | string): string =>
      bottom.gt(0) ? bottom.plus(value).toFormat(0) : String(value),
    [bottom],
  );

  const renderTooltip = useCallback(
    (datum: BarDatumRecord) => (
      <BarTooltip data={datum} aggregationInterval={aggregationInterval} bottom={bottom} />
    ),
    [aggregationInterval, bottom],
  );

  const loading = Boolean(isLoading || !chartData);

  return (
    <div className="flex-grow [&_rect]:transition [&_rect]:duration-150 [&_rect]:ease-[cubic-bezier(0.550,0.055,0.675,0.19)]">
      <BarChart
        chartId="stake-balance"
        className="-mx-4 h-[300px]"
        data={formattedData}
        keys={['balance']}
        barColors={[color]}
        chartDates={chartDates}
        isLoading={loading}
        margin={{ top: 20, right: 20, bottom: 50, left: 60 }}
        padding={0.1}
        xTickFormat={xTickFormat}
        yTickFormat={yTickFormat}
        renderTooltip={renderTooltip}
      />
      <div className="flex justify-between">
        <ChartLegend
          items={[
            {
              label: <div className="flex items-center gap-x-1">{FLIP_SYMBOL} Balance</div>,
              color,
            },
          ]}
        />
        <ExportCsvButton
          label="Export CSV"
          canExport={Boolean(exportCsvData)}
          onExport={() => exportCsvData?.()}
        />
      </div>
    </div>
  );
};
